/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-icon.svg';
import { ReactComponent as ChevronDownIcon } from '../../../../assets/icons/down-arrow.svg';
import { ReactComponent as SearchIcon } from '../../../../assets/icons/search-icon.svg';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { fetchFacilitiesHierarchy } from '../../../../redux/slices/CustomDashboardSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomCheckbox from '../../../../shared-components/custom-check-box/CustomCheckBox';

interface DashboardFilterProperties {
  onClose?: () => void;
  onApply?: (filters: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  }) => void;
  initialFilters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * Dashboard Filter component matching the "Bookmarks & Preferences" UI.
 * @param {any} param0 - The param0
 * @param {any} param0.onClose - The onClose prop
 * @param {any} param0.onApply - The onApply prop
 * @param {any} param0.initialFilters - The initialFilters prop
 * @returns {React.JSX.Element} The rendered dashboard filter component
 */
function DashboardFilter({ onClose, onApply, initialFilters }: DashboardFilterProperties) {
  const { t, i18n } = useTranslation('dashboard');
  const isRtl =
    i18n.language === 'ar' || (typeof i18n.dir === 'function' && i18n.dir() === 'rtl');
  const dispatch = useAppDispatch();
  const { facilitiesHierarchy } = useAppSelector((state) => state.customDashboard);

  const [activeTab, setActiveTab] = useState<'advance' | 'bookmark'>('advance');
  const [personalChecked, setPersonalChecked] = useState(
    initialFilters?.type?.includes('personal') ?? false,
  );
  const [commercialChecked, setCommercialChecked] = useState(
    initialFilters?.type?.includes('commercial') ?? true,
  );

  // Search states
  const [facilitySearch, setFacilitySearch] = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');
  const [debouncedFacilitySearch, setDebouncedFacilitySearch] = useState('');
  const [debouncedDeviceSearch, setDebouncedDeviceSearch] = useState('');

  // Expanded nodes for the tree view
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFacilitySearch(facilitySearch);
    }, 300);
    return () => clearTimeout(handler);
  }, [facilitySearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedDeviceSearch(deviceSearch);
    }, 300);
    return () => clearTimeout(handler);
  }, [deviceSearch]);

  // Selection states
  const [allFacilitiesChecked, setAllFacilitiesChecked] = useState(false);
  const [facilityStates, setFacilityStates] = useState<Record<string, boolean>>(() => {
    const states: Record<string, boolean> = {};
    initialFilters?.facilityIds?.forEach((id) => {
      states[id] = true;
    });
    return states;
  });
  const [stationStates, setStationStates] = useState<Record<string, boolean>>(() => {
    const states: Record<string, boolean> = {};
    initialFilters?.stationIds?.forEach((id) => {
      states[id] = true;
    });
    return states;
  });
  const [deviceStates, setDeviceStates] = useState<Record<string, boolean>>(() => {
    const states: Record<string, boolean> = {};
    initialFilters?.deviceIds?.forEach((id) => {
      states[id] = true;
    });
    return states;
  });

  // Active station for device column
  const [activeStationId, setActiveStationId] = useState<string | null>(null);

  // Sidebar visibility state
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  useEffect(() => {
    if (!facilitiesHierarchy) {
      dispatch(fetchFacilitiesHierarchy());
    }
  }, [dispatch, facilitiesHierarchy]);

  // Set default active station and expanded node once data is loaded
  useEffect(() => {
    if (facilitiesHierarchy?.facilities && facilitiesHierarchy.facilities.length > 0) {
      if (!activeStationId && (facilitiesHierarchy.facilities[0].stations?.length ?? 0) > 0) {
        setActiveStationId(facilitiesHierarchy.facilities[0].stations[0].id);
      }
      if (expandedNodes.length === 0) {
        setExpandedNodes([facilitiesHierarchy.facilities[0].id]);
      }
    }
  }, [facilitiesHierarchy, activeStationId, expandedNodes.length]);

  /**
   * Filtered facilities based on search
   */
  const filteredFacilities = useMemo(() => {
    if (!facilitiesHierarchy?.facilities) return [];
    if (!debouncedFacilitySearch) return facilitiesHierarchy.facilities;

    const lowerSearch = debouncedFacilitySearch?.toLowerCase();
    return facilitiesHierarchy.facilities.filter(
      (f) =>
        f?.name?.toLowerCase()?.includes(lowerSearch) ||
        f?.stations?.some((s) => s?.name?.toLowerCase()?.includes(lowerSearch)),
    );
  }, [facilitiesHierarchy, debouncedFacilitySearch]);

  /**
   * Get devices for currently active station, filtered by search
   */
  const activeStation = useMemo(() => {
    return facilitiesHierarchy?.facilities
      ?.flatMap((f) => f.stations)
      ?.find((s) => s.id === activeStationId);
  }, [facilitiesHierarchy, activeStationId]);

  const filteredDevices = useMemo(() => {
    if (!activeStation?.devices) return [];
    if (!debouncedDeviceSearch) return activeStation.devices;

    const lowerSearch = debouncedDeviceSearch?.toLowerCase();
    return activeStation.devices.filter((d) =>
      d.chargingPointSn?.toLowerCase()?.includes(lowerSearch),
    );
  }, [activeStation, debouncedDeviceSearch]);

  const totalFacilitiesCount = useMemo(() => {
    return facilitiesHierarchy?.facilities?.length || 0;
  }, [facilitiesHierarchy]);

  /**
   * Toggles the expanded state of a tree node
   * @param id The id of the node to toggle
   */
  const toggleNode = useCallback((id: string) => {
    setExpandedNodes((previous) =>
      previous.includes(id) ? previous.filter((nodeId) => nodeId !== id) : [...previous, id],
    );
  }, []);

  /**
   * Handles toggling the "All Facilities" checkbox
   * @param checked The new checked state
   */
  const handleAllFacilitiesToggle = useCallback(
    (checked: boolean) => {
      if (!facilitiesHierarchy) return;
      setAllFacilitiesChecked(checked);
      const newFacilityStates: Record<string, boolean> = {};
      const newStationStates: Record<string, boolean> = {};
      const newDeviceStates: Record<string, boolean> = {};

      facilitiesHierarchy.facilities.forEach((facility) => {
        newFacilityStates[facility.id] = checked;
        facility.stations.forEach((station) => {
          newStationStates[station.id] = checked;
          station.devices.forEach((device) => {
            newDeviceStates[device.id] = checked;
          });
        });
      });

      setFacilityStates(newFacilityStates);
      setStationStates(newStationStates);
      setDeviceStates(newDeviceStates);
    },
    [facilitiesHierarchy],
  );

  /**
   * Handles toggling a specific facility
   * @param facilityId The id of the facility
   * @param checked The new checked state
   */
  const handleFacilityToggle = useCallback(
    (facilityId: string, checked: boolean) => {
      if (!facilitiesHierarchy) return;
      const facility = facilitiesHierarchy.facilities.find((f) => f.id === facilityId);
      if (!facility) return;

      setFacilityStates((previous: Record<string, boolean>) => ({
        ...previous,
        [facilityId]: checked,
      }));

      const newStationStates = { ...stationStates };
      const newDeviceStates = { ...deviceStates };

      facility.stations.forEach((station) => {
        newStationStates[station.id] = checked;
        station.devices.forEach((device) => {
          newDeviceStates[device.id] = checked;
        });
      });

      setStationStates(newStationStates);
      setDeviceStates(newDeviceStates);
    },
    [facilitiesHierarchy, stationStates, deviceStates],
  );

  /**
   * Handles toggling a specific station
   * @param stationId The id of the station
   * @param checked The new checked state
   */
  const handleStationToggle = useCallback(
    (stationId: string, checked: boolean) => {
      if (!facilitiesHierarchy) return;
      setStationStates((previous: Record<string, boolean>) => ({
        ...previous,
        [stationId]: checked,
      }));

      const station = facilitiesHierarchy.facilities
        .flatMap((f) => f.stations)
        .find((s) => s.id === stationId);

      if (station) {
        const newDeviceStates = { ...deviceStates };
        station.devices.forEach((device) => {
          newDeviceStates[device.id] = checked;
        });
        setDeviceStates(newDeviceStates);
      }
    },
    [facilitiesHierarchy, deviceStates],
  );

  /**
   * Handles toggling a specific device
   * @param stationId The id of the station containing the device
   * @param deviceId The device identifier
   * @param checked The new checked state
   */
  const handleDeviceToggle = useCallback((deviceId: string, checked: boolean) => {
    setDeviceStates((previous: Record<string, boolean>) => ({ ...previous, [deviceId]: checked }));
  }, []);

  /**
   * Clears all filter selections
   */
  const handleClearFilters = useCallback(() => {
    setAllFacilitiesChecked(false);
    setFacilityStates({});
    setStationStates({});
    setDeviceStates({});
    setPersonalChecked(false);
    setCommercialChecked(true);
  }, []);

  /**
   * Memoized Device Item component
   */
  const DeviceItem = useMemo(
    () =>
      React.memo(
        ({
          device,
          isChecked,
          onToggleCheck,
        }: {
          device: any;
          isChecked: boolean;
          onToggleCheck: (id: string, checked: boolean) => void;
        }) => (
          <div className="node-content">
            <CustomCheckbox
              name={device.id}
              checkboxLabel={device.chargingPointSn}
              value={isChecked}
              onChangeValue={(_, value) => onToggleCheck(device.id, value)}
            />
          </div>
        ),
      ),
    [],
  );

  /**
   * Memoized Station Item component
   */
  const StationItem = useMemo(
    () =>
      React.memo(
        ({
          station,
          isActive,
          onSetActive,
          isChecked,
          onToggleCheck,
        }: {
          station: any;
          isActive: boolean;
          onSetActive: (id: string) => void;
          isChecked: boolean;
          onToggleCheck: (id: string, checked: boolean) => void;
        }) => (
          <div
            className={`node-content ${isActive ? 'active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => onSetActive(station.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                onSetActive(station.id);
              }
            }}>
            <CustomCheckbox
              name={station.id}
              checkboxLabel={station.name}
              value={isChecked}
              onChangeValue={(_, value) => onToggleCheck(station.id, value)}
            />
            <span className="item-count">{station.count}</span>
          </div>
        ),
      ),
    [],
  );

  /**
   * Memoized Facility Item component
   */
  const FacilityItem = useMemo(
    () =>
      React.memo(
        ({
          facility,
          isExpanded,
          onToggleExpand,
          isChecked,
          onToggleFacility,
          onToggleStation,
          activeStationId: currentActiveStationId,
          setActiveStationId: onSetActiveStationId,
          stationStates: currentStationStates,
        }: {
          facility: any;
          isExpanded: boolean;
          onToggleExpand: (id: string) => void;
          isChecked: boolean;
          onToggleFacility: (id: string, checked: boolean) => void;
          onToggleStation: (id: string, checked: boolean) => void;
          activeStationId: string | null;
          setActiveStationId: (id: string) => void;
          stationStates: Record<string, boolean>;
        }) => (
          <div className="tree-node">
            <div className="node-content">
              <ChevronDownIcon
                className={`node-icon ${isExpanded ? 'expanded' : ''}`}
                onClick={() => onToggleExpand(facility.id)}
              />
              <CustomCheckbox
                name={facility.id}
                checkboxLabel={facility.name}
                value={isChecked}
                onChangeValue={(_, value) => onToggleFacility(facility.id, value)}
              />
              <span className="item-count">{facility.count}</span>
            </div>

            {isExpanded && (
              <div className="node-children">
                <div className="node-content">
                  <CustomCheckbox
                    name={`${facility.id}-all`}
                    checkboxLabel={t('allStation')}
                    value={isChecked}
                    onChangeValue={(_, value) => onToggleFacility(facility.id, value)}
                  />
                </div>
                {facility.stations.map((station: any) => (
                  <StationItem
                    key={station.id}
                    station={station}
                    isActive={currentActiveStationId === station.id}
                    onSetActive={onSetActiveStationId}
                    isChecked={currentStationStates[station.id] || false}
                    onToggleCheck={onToggleStation}
                  />
                ))}
              </div>
            )}
          </div>
        ),
      ),
    [StationItem, t],
  );

  return (
    <div
      className={`dashboard-filter-popover ${isRtl ? 'rtl' : ''}`}
      dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="filter-header">
        <h2>{t('bookmarksAndPreferences')}</h2>
        <CloseIcon className="close-btn" onClick={onClose} />
      </div>

      <div className="filter-tabs">
        <div
          role="tab"
          aria-selected={activeTab === 'advance'}
          tabIndex={0}
          className={`tab ${activeTab === 'advance' ? 'active' : ''}`}
          onClick={() => setActiveTab('advance')}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setActiveTab('advance');
            }
          }}>
          {t('advanceSearch')}
        </div>
        {/* <div
          className={`tab ${activeTab === 'bookmark' ? 'active' : ''}`}
          onClick={() => setActiveTab('bookmark')}
        >
          {t('savedBookmark')}
        </div>
        <div className="bookmark-icon-shortcut">
          <SaveBookMarkIcon />
        </div> */}
      </div>

      <div className="show-data-only">
        <span id="show-data-only-label" className="show-data-only-label">
          {t('showDataOnly')}
        </span>
        <div className="checkbox-group" aria-labelledby="show-data-only-label">
          <CustomCheckbox
            checkboxLabel={t('personal')}
            name="personal"
            value={personalChecked}
            onChangeValue={(_, value) => setPersonalChecked(value)}
          />
          <CustomCheckbox
            checkboxLabel={t('commercial')}
            name="commercial"
            value={commercialChecked}
            onChangeValue={(_, value) => setCommercialChecked(value)}
          />
        </div>
      </div>
      <div
        className={`filter-columns ${!commercialChecked ? 'personal-mode' : ''} ${!isSidebarVisible ? 'sidebar-hidden' : ''}`}>
        {commercialChecked && (
          <>
            {isSidebarVisible ? (
              <div className="column sidebar-column">
                <div className="column-header">
                  <h3>{t('searchCriteria')}</h3>
                  <span
                    className="hide-toggle"
                    role="button"
                    tabIndex={0}
                    onClick={() => setIsSidebarVisible(false)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        setIsSidebarVisible(false);
                      }
                    }}>
                    {t('hide')}
                  </span>
                </div>
                <div className="items-list">
                  <div className="list-item active">
                    <span className="item-label">{t('facility')}</span>
                    <span className="item-count">{totalFacilitiesCount}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="sidebar-expand-btn"
                role="button"
                tabIndex={0}
                onClick={() => setIsSidebarVisible(true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setIsSidebarVisible(true);
                  }
                }}>
                {t('expandSearch')}
              </div>
            )}
            {/* Column 2: Select Facility */}
            <div className="column">
              <h3>{t('selectFacility')}</h3>
              <div className="search-container">
                <input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={facilitySearch}
                  onChange={(event) => setFacilitySearch(event.target.value)}
                />
                <SearchIcon className="search-icon" />
              </div>
              <div className="items-list">
                <div className="tree-node">
                  <div className="node-content">
                    <CustomCheckbox
                      name="allFacilities"
                      checkboxLabel={t('allFacilities')}
                      value={allFacilitiesChecked}
                      onChangeValue={(_, value) => handleAllFacilitiesToggle(value)}
                    />
                  </div>
                </div>

                {filteredFacilities.map((facility) => (
                  <FacilityItem
                    key={facility.id}
                    facility={facility}
                    isExpanded={expandedNodes.includes(facility.id)}
                    onToggleExpand={toggleNode}
                    isChecked={facilityStates[facility.id] || false}
                    onToggleFacility={handleFacilityToggle}
                    onToggleStation={handleStationToggle}
                    activeStationId={activeStationId}
                    setActiveStationId={setActiveStationId}
                    stationStates={stationStates}
                  />
                ))}
              </div>
            </div>

            {/* Column 3: Select Device */}
            <div className="column">
              <h3>{t('selectDevice')}</h3>
              <div className="search-container">
                <input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={deviceSearch}
                  onChange={(event) => setDeviceSearch(event.target.value)}
                />
                <SearchIcon className="search-icon" />
              </div>
              <div className="items-list">
                <div className="node-content">
                  <CustomCheckbox
                    name="allDevices"
                    checkboxLabel={t('allDevice')}
                    value={
                      activeStation ? activeStation.devices.every((d) => deviceStates[d.id]) : false
                    }
                    onChangeValue={(_, value) => {
                      if (activeStation) handleStationToggle(activeStation.id, value);
                    }}
                  />
                </div>
                {filteredDevices.map((device) => (
                  <DeviceItem
                    key={device.id}
                    device={device}
                    isChecked={deviceStates[device.id] || false}
                    onToggleCheck={handleDeviceToggle}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="filter-footer">
        <div className="footer-left">
          <MyButton
            label={t('clearAll')}
            buttonType="outline"
            labelColorFill="var(--primary-color)"
            onClickFunc={handleClearFilters}
          />
        </div>
        <div className="footer-right">
          <MyButton
            label={t('cancel')}
            buttonType="outline"
            labelColorFill="var(--primary-color)"
            onClickFunc={onClose}
          />
          <MyButton
            label={t('apply')}
            buttonType="submit"
            onClickFunc={() => {
              const selectedFacilityIds = Object.entries(facilityStates)
                .filter(([, v]) => v)
                .map(([k]) => k);
              const selectedStationIds = Object.entries(stationStates)
                .filter(([, v]) => v)
                .map(([k]) => k);
              const selectedDeviceIds = Object.entries(deviceStates)
                .filter(([, v]) => v)
                .map(([k]) => k);
              const selectedTypes: string[] = [];
              if (commercialChecked) selectedTypes.push('commercial');
              if (personalChecked) selectedTypes.push('personal');

              onApply?.({
                facilityIds: selectedFacilityIds,
                stationIds: selectedStationIds,
                deviceIds: selectedDeviceIds,
                type: selectedTypes,
              });
              onClose?.();
            }}
          />
        </div>
      </div>
    </div>
  );
}

DashboardFilter.defaultProps = {
  onClose: undefined,
  onApply: undefined,
  initialFilters: undefined,
};

export default DashboardFilter;
